import { useState } from 'react'

import { isValidUrl, makeErrorForKey } from '../../utils'

import type { GivingHelpLinkConfigValue } from '@percent/admin-dashboard/api/types'

export type EditingKey = 'helpLink' | null

// Named so the single writable boolean key lives in one place; widen this union as more
// Behaviour boolean toggles land rather than editing the field inline.
export type BooleanToggleKey = 'require_organisation_time_log'

export type PendingBooleanToggle = {
  key: BooleanToggleKey
  label: string
  newEnabled: boolean
  onConfirm: () => void
}

type BehaviourSectionData = {
  helpLinkData?: GivingHelpLinkConfigValue
  savingKey: string | null
  isSaving: boolean
  mutationError?: string
  resetSaving?: () => void
}

export function useBehaviourSection({
  helpLinkData,
  savingKey,
  isSaving,
  mutationError,
  resetSaving
}: BehaviourSectionData) {
  const [editingKey, setEditingKey] = useState<EditingKey>(null)
  const [pendingToggle, setPendingToggle] = useState<PendingBooleanToggle | null>(null)

  const [helpLinkDraft, setHelpLinkDraft] = useState<GivingHelpLinkConfigValue>({ link: '', title: '' })
  // Touched flags live in the hook (not the section) so behaviour's draft/validation state stays colocated.
  const [helpLinkUrlTouched, setHelpLinkUrlTouched] = useState(false)
  const [helpLinkTitleTouched, setHelpLinkTitleTouched] = useState(false)

  const startEditing = (key: EditingKey) => {
    setEditingKey(key)

    if (key === 'helpLink') {
      setHelpLinkDraft(helpLinkData ?? { link: '', title: '' })
      setHelpLinkUrlTouched(false)
      setHelpLinkTitleTouched(false)
    }
  }

  const cancel = () => {
    resetSaving?.()
    setEditingKey(null)
  }

  const errorForKey = makeErrorForKey(savingKey, isSaving, mutationError)

  const helpLinkUrlInvalid = !helpLinkDraft.link || !isValidUrl(helpLinkDraft.link)
  const showHelpLinkUrlError = helpLinkUrlInvalid && helpLinkUrlTouched
  const helpLinkTitleInvalid = !helpLinkDraft.title.trim()
  const showHelpLinkTitleError = helpLinkTitleInvalid && helpLinkTitleTouched

  const handleBooleanToggle = (
    key: PendingBooleanToggle['key'],
    label: string,
    currentValue: boolean | undefined,
    saveFn: (value: boolean, onSuccess?: () => void) => void
  ) => {
    const newEnabled = !(currentValue ?? false)

    setPendingToggle({
      key,
      label,
      newEnabled,
      onConfirm: () => {
        saveFn(newEnabled, () => setPendingToggle(null))
      }
    })
  }

  return {
    editingKey,
    startEditing,
    cancel,
    errorForKey,
    pendingToggle,
    setPendingToggle,
    handleBooleanToggle,

    helpLinkUrlInvalid,
    showHelpLinkUrlError,
    setHelpLinkUrlTouched,
    helpLinkTitleInvalid,
    showHelpLinkTitleError,
    setHelpLinkTitleTouched,

    helpLinkDraft,
    setHelpLinkDraft
  }
}
